import type { Post } from "./model.ts";

export interface ThreadRow {
  post: Post;
  parent?: string;
  depth: number;
  indent: number;
  connectedToParent: boolean;
  detached: boolean;
  issue?: string;
}

export function missingParents(
  root: string,
  posts: Map<string, Post>,
): string[] {
  return [
    ...new Set(
      [...posts.values()].flatMap(({ uri, record }) =>
        uri !== root && record?.reply?.root.uri === root &&
          !posts.has(record.reply.parent.uri)
          ? [record.reply.parent.uri]
          : []
      ),
    ),
  ];
}

export function threadTree(
  root: string,
  posts: Map<string, Post>,
): ThreadRow[] {
  const nodes = new Map<
    string,
    { post: Post; parent?: string; children: string[]; issue?: string }
  >();
  for (const [uri, post] of posts) nodes.set(uri, { post, children: [] });
  if (!nodes.has(root)) {
    nodes.set(root, {
      post: { uri: root, error: "Root unavailable." },
      children: [],
    });
  }
  for (const [uri, node] of [...nodes]) {
    if (uri === root) continue;
    const reply = node.post.record?.reply;
    if (!node.post.record) continue;
    if (reply?.root.uri !== root) {
      node.issue = "This record no longer references this thread root.";
      continue;
    }
    node.parent = reply.parent.uri;
    if (!nodes.has(node.parent)) {
      nodes.set(node.parent, {
        post: { uri: node.parent, error: "Parent not loaded yet." },
        children: [],
      });
    }
  }
  // Break one edge per cycle, keeping every record visible and traversal finite.
  const visited = new Set<string>();
  for (const start of [...nodes.keys()].sort()) {
    const path = new Set<string>();
    let uri: string | undefined = start;
    while (uri && !visited.has(uri)) {
      if (path.has(uri)) {
        const node = nodes.get(uri)!;
        node.parent = undefined;
        node.issue = "Cyclic reply reference; this branch cannot be attached.";
        break;
      }
      path.add(uri);
      uri = nodes.get(uri)?.parent;
    }
    for (const uri of path) visited.add(uri);
  }
  const compare = (a: string, b: string) => {
    const aTime = Date.parse(nodes.get(a)!.post.record?.createdAt ?? "");
    const bTime = Date.parse(nodes.get(b)!.post.record?.createdAt ?? "");
    return (Number.isFinite(aTime) ? aTime : 0) -
        (Number.isFinite(bTime) ? bTime : 0) || (a < b ? -1 : a > b ? 1 : 0);
  };
  const detached: string[] = [];
  for (const [uri, node] of nodes) {
    if (node.parent) nodes.get(node.parent)!.children.push(uri);
    else if (uri !== root) detached.push(uri);
  }
  for (const node of nodes.values()) node.children.sort(compare);
  detached.sort(compare);
  const stack = [root, ...detached].reverse().map((uri) => ({
    uri,
    depth: 0,
    indent: 0,
    detached: uri !== root,
  }));
  const rows: ThreadRow[] = [];
  while (stack.length) {
    const { uri, depth, indent, detached } = stack.pop()!;
    const node = nodes.get(uri)!;
    rows.push({
      post: node.post,
      parent: node.parent,
      depth,
      indent,
      connectedToParent: !!node.parent &&
        nodes.get(node.parent)!.children.length === 1,
      detached,
      issue: node.issue,
    });
    for (let i = node.children.length - 1; i >= 0; i--) {
      stack.push({
        uri: node.children[i],
        depth: depth + 1,
        indent: indent + (node.children.length > 1 ? 1 : 0),
        detached,
      });
    }
  }
  return rows;
}
